import { Layer } from "../types/Editor";
import React from "react";
import {
  AbsoluteFill,
  OffthreadVideo,
  Sequence,
  useVideoConfig,
  useCurrentFrame,
  interpolate,
} from "remotion";
import { BlurEffect } from "./BlurEffect";
import { HighlightEffect } from "./HighlightEffect";

interface VideoWithEffectsProps {
  videoSrc: string | null;
  layers: Layer[];
  isPaused: boolean;
  onLayerUpdate: (updatedLayer: Layer) => void;
  activeLayerId?: string;
  isDrawing: boolean;
  onMouseDown: (
    e: React.MouseEvent,
    layer: Layer,
    action: "drag" | "resize",
    handle?: string
  ) => void;
  isDragging: boolean;
  isResizing: string | null;
}

export const VideoWithEffects: React.FC<VideoWithEffectsProps> = ({
  videoSrc,
  layers,
  isPaused,
  activeLayerId,
  onMouseDown,
  isDragging,
  isResizing,
}) => {
  const { fps, width, height } = useVideoConfig();
  const frame = useCurrentFrame();

  const sortedLayers = [...layers].sort((a, b) => a.start - b.start);

  // Calculate zoom transformation for current frame
  const getZoomTransform = () => {
    const activeZoomLayer = sortedLayers.find((layer) => {
      if (layer.type !== "zoom") return false;

      const layerStartFrame = Math.floor(layer.start * fps);
      const layerEndFrame =
        layerStartFrame +
        Math.ceil(
          (layer.introDuration + layer.mainDuration + layer.outroDuration) * fps
        );

      return frame >= layerStartFrame && frame < layerEndFrame;
    });

    if (!activeZoomLayer) {
      return { scale: 1, translateX: 0, translateY: 0 };
    }

    const layerStartFrame = Math.floor(activeZoomLayer.start * fps);
    const localFrame = frame - layerStartFrame;

    const introEndFrame = activeZoomLayer.introDuration * fps;
    const mainEndFrame = introEndFrame + activeZoomLayer.mainDuration * fps;
    const outroEndFrame = mainEndFrame + activeZoomLayer.outroDuration * fps;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    let easedProgress = 0;
    if (localFrame <= introEndFrame) {
      easedProgress = easeInOutCubic(
        interpolate(localFrame, [0, introEndFrame], [0, 1], {
          extrapolateRight: "clamp",
        })
      );
    } else if (localFrame <= mainEndFrame) {
      easedProgress = 1;
    } else if (localFrame <= outroEndFrame) {
      easedProgress = easeInOutCubic(
        interpolate(localFrame, [mainEndFrame, outroEndFrame], [1, 0], {
          extrapolateRight: "clamp",
        })
      );
    }

    const scale = interpolate(
      easedProgress,
      [0, 1],
      [1, activeZoomLayer.data.zoomFactor || 1.5]
    );

    // Use centerX/centerY if available, otherwise fallback to region center
    const layerCenterX =
      activeZoomLayer.data.centerX !== undefined
        ? activeZoomLayer.data.centerX
        : activeZoomLayer.data.x + activeZoomLayer.data.width / 2;

    const layerCenterY =
      activeZoomLayer.data.centerY !== undefined
        ? activeZoomLayer.data.centerY
        : activeZoomLayer.data.y + activeZoomLayer.data.height / 2;

    let translateX = -width * layerCenterX * (scale - 1);
    let translateY = -height * layerCenterY * (scale - 1);

    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    const minTranslateX = Math.min(0, width - scaledWidth);
    const maxTranslateX = 0;
    const minTranslateY = Math.min(0, height - scaledHeight);
    const maxTranslateY = 0;

    translateX = Math.min(maxTranslateX, Math.max(minTranslateX, translateX));
    translateY = Math.min(maxTranslateY, Math.max(minTranslateY, translateY));

    return { scale, translateX, translateY };
  };

  const zoomTransform = getZoomTransform();

  const getHandleStyle = (handle: string) => {
    const baseStyle = {
      position: "absolute" as const,
      backgroundColor: "#ffffff",
      border: "2px solid #007acc",
      borderRadius: "4px",
      cursor: `${handle}-resize`,
      zIndex: 1001,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
    };

    if (handle.includes("-")) {
      return {
        ...baseStyle,
        width: "20px",
        height: "20px",
        ...(handle.includes("top") && { top: "-10px" }),
        ...(handle.includes("bottom") && { bottom: "-10px" }),
        ...(handle.includes("left") && { left: "-10px" }),
        ...(handle.includes("right") && { right: "-10px" }),
      };
    }

    const edgeStyle = {
      ...baseStyle,
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      border: "1px solid #007acc",
    };

    switch (handle) {
      case "top":
      case "bottom":
        return {
          ...edgeStyle,
          width: "60px",
          height: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          ...(handle === "top" && { top: "-5px" }),
          ...(handle === "bottom" && { bottom: "-5px" }),
        };
      case "left":
      case "right":
        return {
          ...edgeStyle,
          width: "10px",
          height: "60px",
          top: "50%",
          transform: "translateY(-50%)",
          ...(handle === "left" && { left: "-5px" }),
          ...(handle === "right" && { right: "-5px" }),
        };
      default:
        return baseStyle;
    }
  };

  const getEffectsController = (layer) => {
    return (
      <div
        style={{
          position: "absolute",
          left: `${layer.data.x * 100}%`,
          top: `${layer.data.y * 100}%`,
          width: `${layer.data.width * 100}%`,
          height: `${layer.data.height * 100}%`,
          cursor: isDragging ? "grabbing" : "grab",
          border:
            layer.id === activeLayerId
              ? "2px solid rgba(0, 122, 204, 0.8)"
              : "1px solid rgba(255, 255, 255, 0.3)",
          backgroundColor:
            layer.id === activeLayerId
              ? "rgba(0, 122, 204, 0.1)"
              : "transparent",
          pointerEvents: "all",
          zIndex: layer.id === activeLayerId ? 1000 : 1,
          boxShadow:
            layer.id === activeLayerId
              ? "0 0 0 1px rgba(0, 122, 204, 0.5)"
              : "none",
        }}
        onMouseDown={(e) => onMouseDown(e, layer, "drag")}
      >
        {layer.id === activeLayerId && (
          <>
            {layer.type !== "zoom" ? (
              <>
                {["top-left", "top-right", "bottom-left", "bottom-right"].map(
                  (handle) => (
                    <div
                      key={handle}
                      style={getHandleStyle(handle)}
                      onMouseDown={(e) =>
                        onMouseDown(e, layer, "resize", handle)
                      }
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.transform =
                          "scale(1.1)";
                        (e.target as HTMLElement).style.backgroundColor =
                          "#007acc";
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.transform = "scale(1)";
                        (e.target as HTMLElement).style.backgroundColor =
                          "#ffffff";
                      }}
                    />
                  )
                )}
                {["top", "right", "bottom", "left"].map((handle) => (
                  <div
                    key={handle}
                    style={getHandleStyle(handle)}
                    onMouseDown={(e) => onMouseDown(e, layer, "resize", handle)}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor =
                        "rgba(0, 122, 204, 0.8)";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.backgroundColor =
                        "rgba(255, 255, 255, 0.9)";
                    }}
                  />
                ))}
              </>
            ) : (
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "20px",
                  height: "20px",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "transparent",
                  zIndex: 1001,
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "0",
                    width: "2px",
                    height: "100%",
                    backgroundColor: "#007acc",
                    transform: "translateX(-50%)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "0",
                    width: "100%",
                    height: "2px",
                    backgroundColor: "#007acc",
                    transform: "translateY(-50%)",
                  }}
                />
              </div>
            )}
            <div
              style={{
                position: "absolute",
                top: "-30px",
                left: "0",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                zIndex: 1002,
              }}
            >
              {layer.type}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <AbsoluteFill>
      {videoSrc && (
        <AbsoluteFill style={{ overflow: "hidden" }}>
          <AbsoluteFill
            style={{
              transform: `translate(${zoomTransform.translateX}px, ${zoomTransform.translateY}px) scale(${zoomTransform.scale})`,
              transformOrigin: "top left",
              willChange: "transform",
            }}
          >
            <OffthreadVideo
              src={videoSrc}
              pauseWhenBuffering
              acceptableTimeShiftInSeconds={0.5}
            />
            {sortedLayers
              .filter((layer) => layer.type === "blur")
              .map((layer) => (
                <Sequence
                  key={layer.id}
                  from={Math.floor(layer.start * fps)}
                  durationInFrames={Math.ceil(
                    (layer.introDuration +
                      layer.mainDuration +
                      layer.outroDuration) *
                    fps
                  )}
                >
                  <BlurEffect layer={layer} />
                  {isPaused && getEffectsController(layer)}
                </Sequence>
              ))}
          </AbsoluteFill>
        </AbsoluteFill>
      )}

      {/* Highlight effects (not zoomed) */}
      {sortedLayers
        .filter((layer) => layer.type === "highlight")
        .map((layer) => (
          <Sequence
            from={Math.floor(layer.start * fps)}
            durationInFrames={Math.ceil(
              (layer.introDuration + layer.mainDuration + layer.outroDuration) *
              fps
            )}
            key={layer.id}
          >
            <HighlightEffect layer={layer} />
            {isPaused && getEffectsController(layer)}
          </Sequence>
        ))}
    </AbsoluteFill>
  );
};
